/**
 * EntitySuggestionCard - Displays AI-suggested entity with actions
 * Shows type badge, label, description, confidence, and Create/Refine buttons
 */

import { Sparkles, Plus, RefreshCw } from 'lucide-react'
import type { SuggestedEntity } from '@/hooks/useWorkspaceCoach'

interface EntitySuggestionCardProps {
  suggestion: SuggestedEntity
  onAccept: () => void
  onRefine: () => void
  isCreating?: boolean
}

// Map entity types to domain colors
const entityTypeColors: Record<string, string> = {
  CustomerSegment: 'var(--color-market)',
  Persona: 'var(--color-market)',
  CustomerPain: 'var(--color-market)',
  Industry: 'var(--color-market)',
  Competitor: 'var(--color-market)',
  Evidence: 'var(--color-market)',
  Trend: 'var(--color-market)',
  ValueProposition: 'var(--color-business)',
  Channel: 'var(--color-business)',
  RevenueStream: 'var(--color-business)',
  Partnership: 'var(--color-business)',
  Feature: 'var(--color-solution)',
  Requirement: 'var(--color-solution)',
}

export function EntitySuggestionCard({
  suggestion,
  onAccept,
  onRefine,
  isCreating = false,
}: EntitySuggestionCardProps) {
  const typeColor = entityTypeColors[suggestion.type] || 'var(--color-text-muted)'

  // Confidence indicator color
  const confidenceColor = suggestion.confidence >= 80
    ? 'var(--color-market)'
    : suggestion.confidence >= 60
    ? 'var(--color-business)'
    : 'var(--color-text-muted)'

  return (
    <div className="bg-warm-50 border border-border rounded-lg p-4 space-y-3">
      {/* Header with type badge and confidence */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500" />
          <span
            className="text-xs font-medium px-2 py-0.5 rounded-full"
            style={{
              backgroundColor: `color-mix(in srgb, ${typeColor} 15%, transparent)`,
              color: typeColor,
            }}
          >
            {suggestion.type}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: confidenceColor }}
          />
          <span className="text-xs text-text-muted">{suggestion.confidence}% confident</span>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-1">
        <h4 className="font-medium text-sm text-text">{suggestion.label}</h4>
        <p className="text-xs text-text-secondary line-clamp-2">{suggestion.description}</p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={onAccept}
          disabled={isCreating}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium text-white transition-colors disabled:opacity-50"
          style={{ backgroundColor: typeColor }}
        >
          <Plus className="w-3.5 h-3.5" />
          {isCreating ? 'Creating...' : 'Create Entity'}
        </button>
        <button
          onClick={onRefine}
          disabled={isCreating}
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium text-text-secondary bg-warm-100 hover:bg-warm-200 transition-colors disabled:opacity-50"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refine
        </button>
      </div>
    </div>
  )
}
